// Shared CSV helpers — escaping per RFC 4180 plus the browser download
// trigger. Row-projection logic stays with each feature service (e.g.
// campaignRowsToCsv in campaign-service.ts) so this module never grows
// domain knowledge.

/** Quote a CSV cell when it contains a delimiter, quote, or newline. */
export function csvEscape(v: string): string {
  if (/[",\r\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

/**
 * triggerCsvDownload writes the rendered CSV string to a Blob and clicks a
 * transient anchor so the browser saves it. Split from the row projection so
 * that logic stays unit-testable without touching the DOM.
 */
export function triggerCsvDownload(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * The whole "Download CSV" gesture, which clicks, conversions and landers each
 * wrote out again: refuse an empty export with a reason, render, save, and say
 * how much left. Returns whether anything was written.
 */
export function exportCsvRows<T>(
  rows: T[],
  render: (rows: T[]) => string,
  filename: string,
  notify: { error: (m: string) => void; success: (m: string) => void },
): boolean {
  if (rows.length === 0) {
    notify.error("Nothing to export");
    return false;
  }
  triggerCsvDownload(render(rows), filename);
  notify.success(`Exported ${rows.length} ${rows.length === 1 ? "row" : "rows"}`);
  return true;
}
